/* eslint-disable @typescript-eslint/no-namespace */
import type { Seller } from './seller';
import type {
  GetPromotionShelf_getPromotionShelf_productWithBids_adMarker,
  ProductVat,
  ProductPage_product_skuList_paymentOptions,
  getMakeSearch_makeSearch_items_adMarker,
  Cart_cart,
} from '../types/apollo';
import { KOPECKS_IN_RUB } from '@/shared/helpers/business/constant-variables';

export namespace Product {
  export interface Suggestions {
    suggestions: Suggestion[];
  }

  export enum EPhotoType {
    PHOTO_60 = 60,
    PHOTO_80 = 80,
    PHOTO_120 = 120,
    PHOTO_240 = 240,
    PHOTO_480 = 480,
    PHOTO_540 = 540,
    PHOTO_720 = 720,
    PHOTO_800 = 800,
  }

  export enum ESKUGroupType {
    SkuGroup = 'SG',
    SkuOption = 'SO',
    OptionValue = 'OV',
  }

  export interface ICrossSell {
    description: string;
    id: number;
    items: ICrossProduct[];
    title: string;
  }

  export interface ICrossProduct {
    characteristics: ICrossProductCharacteristics[];
    charityCommission: number;
    compressedImageUrl: string;
    fullPrice: number;
    imageUrl: string;
    ordersQuantity: number;
    productId: number;
    rating: number;
    sellPrice: number;
    skuId: number;
    title: string;
    offer?: Offer;
  }

  export interface ICrossProductCharacteristics {
    charTitle: string;
    value: string;
  }

  export interface Suggestion {
    text: string;
    categoryId?: string;
    categoryTitle?: string;
  }

  export interface Category {
    offerCategory?: string;
    offerCategoryTitle?: string;
    id: number;
    title: string;
    children?: Category[];
    seoHeader?: string;
    seoMetaTag?: string;
  }

  export interface ParentedCategory {
    id: number;
    title: string;
    parent?: ParentedCategory;
  }

  export interface Characteristic {
    id: number;
    title: string;
    values: {
      id: number;
      title: string;
      value: string;
    }[];
  }

  export interface DiscountBadge {
    text: string;
    textColor: string;
    backgroundColor: string;
  }

  export interface SKU {
    id?: number;
    characteristics?: {
      charIndex: number;
      valueIndex: number;
    }[];
    availableAmount: number;
    fullPrice: number;
    purchasePrice: number;
    charityProfit?: number;
    offer?: Offer;
    discountBadge?: DiscountBadge;
    vat?: IVat;
    productOptionDtos?: IProductOptionDto[];
    paymentOptions?: ProductPage_product_skuList_paymentOptions[];
  }

  export interface IVat {
    type: TVatType;
    vatRate: number;
    vatAmount: number;
    price: number;
  }

  export const EVatType = {
    NONE: 'NONE',
    VAT0: 'VAT0',
    VAT10: 'VAT10',
    VAT20: 'VAT20',
    VAT110: 'VAT110',
    VAT120: 'VAT120',
  } as const;

  export type TVatType = keyof typeof EVatType;

  export interface ListItem {
    inStock?: boolean;
    productId: number;
    title: string;
    compressedImage: string;
    image: string;
    fullPrice: number;
    sellPrice: number;
    orderItemId?: number;
    ordersQuantity?: number;
    rating?: number;
    isFavourite?: boolean;
    isFavorite?: boolean;
    isEco?: boolean;
    charityCommission?: number;
    offer?: Offer;
    badges?: Badge[];
    isAdultCategory?: boolean;
    skuGroup?: ISkuGroup;
    feedbackQuantity?: number;
    bidId?: number | null;
    adMarker?:
      | GetPromotionShelf_getPromotionShelf_productWithBids_adMarker
      | getMakeSearch_makeSearch_items_adMarker
      | null;
    reviewsQuantity?: number;
    nearestDelivery?: string;
    deliveryDate?: string;
  }

  export interface ISkuGroup {
    skuGroupId: number;
    skuOption: number;
    optionValue: number;
  }

  export interface PurchaseItem {
    skuId: number;
    amount: number;
  }

  export interface BaseReview {
    id?: number;
    date: Date | number;
    content?: string;
    photos?: Photo[];
  }

  export interface Badge {
    backgroundColor?: string;
    description?: string;
    link?: string;
    text?: string;
    textColor?: string;
    type?: string;
    id?: number;
  }

  export interface ShopReply {
    id?: number;
    content?: string;
    photos?: Photo[];
    orderItemId?: number;
    shop?: string;
    edited?: boolean;
    date?: Date | number;
  }

  export interface Review extends BaseReview {
    edited?: boolean;
    rating: number;
    customer: string;
    reply?: ShopReply;
    isAnonymous?: boolean;
  }

  export interface Photo {
    photo: {
      // Typically - 800, 540, 240, 80
      [key: string]: {
        high: string;
        low: string;
      };
    };
    color?: string;
    photoKey: string;
  }

  export interface Offer {
    icon?: string;
    text?: string;
    textColor?: string;
    time?: number;
  }

  export enum ProductStatus {
    CREATED = 'Помещён в корзину',
    SOLD = 'Продан',
    CANCELED = 'Товара нет на складе',
    RETURNED = 'Возврат',
  }

  export interface OrderItem {
    id: number;
    title: string;
    amount: number;
    amountAvailable: number;
    fullPrice: number;
    purchasePrice: number;
    skuId: number;
    isEco?: boolean;
    categoryId?: string;
    purchasePriceDiscounted?: number;
    offer?: Product.Offer;
    characteristics?: object;
    category?: Product.ParentedCategory;
    seller?: Seller;
    vat?: IVat;
  }

  export class CartItem {
    constructor(
      product: Product,
      activeSKU: Product.SKU,
      public amount: number,
      public photo?: Photo,
      public characteristics?: { [key: string]: string },
      category?: Product.ParentedCategory,
      gqlCartItem?: Cart_cart,
    ) {
      // @ts-ignore 
      this.categoryId = product?.category.title || gqlCartItem?.sku.product.category.title;
      // @ts-ignore 
      this.id = product?.id || gqlCartItem?.sku.product.id;
      // @ts-ignore 
      this.amountAvailable = activeSKU?.availableAmount || gqlCartItem?.sku.availableAmount;
      // @ts-ignore 
      this.fullPrice = activeSKU?.fullPrice || gqlCartItem?.sku.fullPrice / KOPECKS_IN_RUB;
      // @ts-ignore 
      this.purchasePrice = activeSKU?.purchasePrice || gqlCartItem?.sku.sellPrice / KOPECKS_IN_RUB;
      // @ts-ignore 
      this.charityCommission = Math.round((activeSKU?.charityProfit * 100) / activeSKU?.purchasePrice);
      // @ts-ignore 
      this.skuId = activeSKU?.id || gqlCartItem?.sku.id;
      // @ts-ignore 
      this.rating = product?.rating || gqlCartItem?.sku.product.rating;
      // @ts-ignore 
      this.seller = product?.seller || gqlCartItem?.sku.product.shop;
      // @ts-ignore 
      this.title = product?.title || gqlCartItem?.sku.product.title;
      this.isEco = product?.isEco;
      this.isSelected = true;
      // @ts-ignore 
      this.offer = activeSKU?.offer || gqlCartItem?.sku.product.offer;
      // @ts-ignore 
      this.purchasePriceDiscounted = activeSKU?.purchasePrice - (product?.comboDiscountPrice || 0);
      // @ts-ignore 
      this.category = product?.category || category || gqlCartItem?.sku.product.category;
      // @ts-ignore 
      this.vat = activeSKU?.vat || (gqlCartItem?.sku.vat as IVat);
      this.badges = product?.badges || gqlCartItem?.sku.product.badges;
    }

    id: number;
    skuId: number;
    title: string;
    seller: Seller;
    fullPrice: number;
    purchasePrice: number;
    amountAvailable: number;
    charityCommission?: number;
    rating?: number;
    isColorPhotoPreview?: boolean;
    isEco?: boolean;
    categoryId?: string;
    isSelected: boolean;
    offer?: Product.Offer;
    dateCreated?: number;
    items?: OrderItem[];
    orderItems?: OrderItem[];
    purchasePriceDiscounted?: number;
    category: Product.ParentedCategory;
    vat?: IVat;
    badges?: Badge[];
  }

  export interface IProductAction {
    icon: string;
    location: string;
    text: string;
    type: string;
  }

  export interface IAddToCart {
    skuId: number;
    id: number;
    amount: number;
    amountAvailable?: number;
    characteristics?: {
      [key: string]: string;
    };
    category?: Product.ParentedCategory;
    combo?: boolean;
  }
}

export interface Product {
  id?: number;
  title: string;
  categoryId: number;
  orderItemId?: number;
  category: Product.ParentedCategory;
  rating?: number;
  externalRating?: boolean;
  badges?: Product.Badge[];
  reviewsAmount?: number;
  ordersAmount?: number;
  rOrdersAmount?: number;
  totalAvailableAmount: number;
  description: string;
  shortDescription?: string;
  comments?: {
    commentType: string;
    comment: string;
  }[];
  attributes?: string[];
  photos: Product.Photo[];
  characteristics?: Product.Characteristic[];
  colorPhotoPreview: boolean;
  skuList: Product.SKU[];
  seller: Seller;
  isFavourite?: boolean;
  isEco?: boolean;
  showKitty?: boolean;
  offer?: Product.Offer;
  adultCategory?: boolean;
  comboDiscountPrice?: number;
  skuGroup?: Product.ISkuGroup;
  circularPhotosList: Product.Photo[];
  productOptionDtos?: IProductOptionDto[];
}

export interface IProductOptionDto {
  active: boolean;
  optionId: number;
  paymentInfo: string;
  text: string;
  titleBanner: string;
  type: EProductOptionDtoTypes;
}

export enum EProductOptionDtoTypes {
  INSTALLMENT = 'INSTALLMENT',
  DOLYAME = 'DOLYAME',
  PODELI = 'PODELI',
}

export type TPaymentDate = {
  amount: string;
  date: string;
};

export interface IProductOptionInfo {
  active: boolean;
  descriptions: {
    active: boolean;
    description: string;
  }[];
  link: string;
  optionId: number;
  price: string;
  subTitle: string;
  tabName: string;
  title: string;
  titleImage: string;
  dolyamePaymentDates?: TPaymentDate[];
  podeliPaymentDates?: TPaymentDate[];
}
